import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { trackReportFeature } from "@/analytics/report-analytics";
import { trackDebounced } from "@/analytics/track-debounce";
import { reactive } from "vue";

export enum ReportSection {
  SELECT_TEMPLATE = "select_template",
  GENERAL = "general",
  PHOTOS = "photos",
  FINDINGS = "findings",
  OVERVIEWS = "overviews",
  CONCLUSIONS = "conclusions",
  APPENDICES = "appendices",
}

export type PhotosViewMode = "gallery" | "details";

// Photo size settings (0-100 scale for uniformity)
export interface PhotoSizeSettings {
  gallery: number; // 0-100 scale, maps to card minWidth
  details: number; // 0-100 scale, maps to image width
  numbering: number; // 0-100 scale, maps to thumbnail size
}

const DEFAULT_PHOTO_SIZES: PhotoSizeSettings = {
  gallery: 50,
  details: 50,
  numbering: 50,
};

const PHOTO_WIDTH = {
  gallery: {
    min: 120,
    max: 500,
  },
  details: {
    min: 50,
    max: 250,
  },
  numbering: {
    min: 30,
    max: 100,
  },
};

interface ReportState {
  activeSection: ReportSection;
  activeTemplateId: number | undefined;
  photosViewMode: PhotosViewMode;
  photoSizes: PhotoSizeSettings;
  photosCollapsedGroups: Set<string>;
  findingsCollapsedGroups: Set<string>;
  findingsUnsortedCollapsed: boolean;
  setActiveSection: (section: ReportSection) => void;
  setActiveTemplate: (templateId: number) => void;
  setPhotosViewMode: (mode: PhotosViewMode) => void;
  setPhotoSize: (key: keyof PhotoSizeSettings, value: number) => void;
  getPhotoWidth: (mode: keyof PhotoSizeSettings) => number;
  togglePhotosGroup: (groupId: string) => void;
  isPhotosGroupCollapsed: (groupId: string) => boolean;
  toggleFindingsGroup: (groupId: string) => void;
  isFindingsGroupCollapsed: (groupId: string) => boolean;
  initialize: () => void;
}

export default reactive<ReportState>({
  activeSection: ReportSection.SELECT_TEMPLATE,
  activeTemplateId: undefined,
  photosViewMode: "gallery",
  photoSizes: { ...DEFAULT_PHOTO_SIZES },
  photosCollapsedGroups: new Set(),
  findingsCollapsedGroups: new Set(),
  findingsUnsortedCollapsed: false,
  setActiveSection(section: ReportSection) {
    this.activeSection = section;
  },
  setActiveTemplate(templateId: number) {
    this.activeTemplateId = templateId;
  },
  setPhotosViewMode(mode: PhotosViewMode) {
    if (mode !== this.photosViewMode) {
      trackReportFeature("view_mode_toggled", { mode });
    }
    this.photosViewMode = mode;
    $ls.setItem(LocalStorageKey.PHOTOS_VIEW_MODE, mode);
  },
  setPhotoSize(key: keyof PhotoSizeSettings, value: number) {
    // Slider-driven: one drag must count once, with the final value.
    trackDebounced("report_photo_size", () =>
      trackReportFeature("photo_size_changed", { view: key, value }),
    );
    this.photoSizes[key] = value;
    $ls.setItem(LocalStorageKey.PHOTO_SIZES, JSON.stringify(this.photoSizes));
  },
  getPhotoWidth(mode: keyof PhotoSizeSettings) {
    const size = this.photoSizes[mode];
    const min = PHOTO_WIDTH[mode].min;
    const max = PHOTO_WIDTH[mode].max;
    return min + (size / 100) * (max - min);
  },
  togglePhotosGroup(groupId: string) {
    if (this.photosCollapsedGroups.has(groupId)) {
      this.photosCollapsedGroups.delete(groupId);
    } else {
      this.photosCollapsedGroups.add(groupId);
    }
  },
  isPhotosGroupCollapsed(groupId: string) {
    return this.photosCollapsedGroups.has(groupId);
  },
  toggleFindingsGroup(groupId: string) {
    if (this.findingsCollapsedGroups.has(groupId)) {
      this.findingsCollapsedGroups.delete(groupId);
    } else {
      this.findingsCollapsedGroups.add(groupId);
    }
  },
  isFindingsGroupCollapsed(groupId: string) {
    return this.findingsCollapsedGroups.has(groupId);
  },
  initialize() {
    const savedMode = $ls.getItem(LocalStorageKey.PHOTOS_VIEW_MODE);
    if (savedMode === "gallery" || savedMode === "details") {
      this.photosViewMode = savedMode;
    }
    const savedSizes = $ls.getItem(LocalStorageKey.PHOTO_SIZES);
    if (savedSizes) {
      try {
        const parsed = JSON.parse(savedSizes);
        this.photoSizes = { ...DEFAULT_PHOTO_SIZES, ...parsed };
      } catch {
        // ignore invalid JSON
      }
    }
  },
});
